"use client";
/*
 * Documentation:
 * Custom Stat Card — https://app.subframe.com/ae91faa555e6/library?component=Custom+Stat+Card_ec6b5f2a-cbac-4a61-abfc-669880be3c72
 * Loader — https://app.subframe.com/ae91faa555e6/library?component=Loader_f2e570c8-e463-45c2-aae9-a960146bc5d5
 */

import React from "react";
import * as SubframeUtils from "../utils";
import * as SubframeCore from "@subframe/core";
import { Loader } from "./Loader";

interface CustomStatCardRootProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  variant?:
    | "dismissed"
    | "at-risk"
    | "resolved"
    | "escalated"
    | "brand"
    | "total"
    | "overdue"
    | "variation"
    | "pending";
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  value?: React.ReactNode;
  secondaryValue?: React.ReactNode;
  secondaryLabel?: React.ReactNode;
  loading?: boolean;
  className?: string;
  selected?: boolean;
  onClick?: () => void;
}

const CustomStatCardRoot = React.forwardRef<
  HTMLElement,
  CustomStatCardRootProps
>(function CustomStatCardRoot(
  {
    variant = "dismissed",
    title,
    subtitle,
    value,
    secondaryValue,
    secondaryLabel,
    loading = false,
    className,
    selected = false,
    onClick,
    ...otherProps
  }: CustomStatCardRootProps,
  ref
) {
  const getBorderClasses = () => {
    if (selected) {
      switch (variant) {
        case "total":
          return "border-2 border-solid border-brand-primary";
        case "escalated":
          return "border-2 border-solid border-warning-600";
        case "resolved":
          return "border-2 border-solid border-success-600";
        case "at-risk":
          return "border-2 border-solid border-error-400";
        case "variation":
        case "overdue":
          return "border-2 border-solid border-neutral-border";
        default:
          return "border-2 border-solid border-neutral-border";
      }
    }
    return "";
  };

  const getHoverClasses = () => {
    if (!onClick) return "";
    
    switch (variant) {
      case "total":
        return "hover:border-2 hover:border-solid hover:border-brand-primary hover:bg-brand-50";
      case "escalated":
        return "hover:border-2 hover:border-solid hover:border-warning-600";
      case "resolved":
        return "hover:border-2 hover:border-solid hover:border-success-600";
      case "at-risk":
        return "hover:border-2 hover:border-solid hover:border-error-400";
      case "variation":
      case "overdue":
        return "hover:border-2 hover:border-solid hover:border-neutral-border";
      default:
        return "hover:border-2 hover:border-solid hover:border-neutral-border";
    }
  };

  return (
    <div
      className={SubframeUtils.twClassNames(
        "group/ec6b5f2a flex w-full flex-col items-start gap-2 rounded-md bg-[#fafafaff] px-5 py-4 transition-all duration-200",
        {
          "cursor-pointer": onClick,
          "bg-[#eff6ffff]": variant === "brand",
          "bg-[#fefce8ff]": variant === "escalated",
          "rounded-md bg-[#f6ffedff] px-5 py-4 shadow-none": variant === "resolved",
          "flex-col flex-nowrap gap-2 rounded-md bg-[#fff1f0ff] px-5 py-4 shadow-none": variant === "at-risk",
          "bg-[#fafafaff]": variant === "pending",
        },
        getBorderClasses(),
        getHoverClasses(),
        className
      )}
      ref={ref as any}
      onClick={onClick}
      {...otherProps}
    >
      <div
        className={SubframeUtils.twClassNames(
          "flex w-full items-center justify-between",
          { flex: variant === "total" || variant === "at-risk" || variant === "pending" }
        )}
      >
        {title ? (
          <div className="flex w-full items-center">
            <span
              className={SubframeUtils.twClassNames(
                "line-clamp-1 text-body-bold font-body-bold text-default-font",
                {
                  "font-['Nunito'] text-[14px] font-[600] leading-[20px] tracking-normal":
                    variant === "at-risk",
                }
              )}
            >
              {title}
            </span>
            {subtitle ? (
              <span className="line-clamp-1 font-['Nunito'] text-[12px] font-[400] leading-[16px] text-default-font">
                {" - "}{subtitle}
              </span>
            ) : null}
          </div>
        ) : null}
        <SubframeCore.Icon
          className={SubframeUtils.twClassNames(
            "hidden text-body font-body text-default-font",
            { hidden: variant === "variation" || variant === "pending" }
          )}
          name="FeatherAlertCircle"
        />
        <SubframeCore.Icon
          className={SubframeUtils.twClassNames(
            "text-body font-body text-default-font",
            {
              hidden:
                variant === "variation" ||
                variant === "total" ||
                variant === "brand" ||
                variant === "escalated" ||
                variant === "resolved" ||
                variant === "at-risk" ||
                variant === "pending",
            }
          )}
          name="FeatherAlertTriangle"
        />
      </div>
      <div className="flex items-center gap-2">
        <div
          className={SubframeUtils.twClassNames(
            "flex flex-col items-start gap-1",
            { hidden: loading }
          )}
        >
          {value !== null && value !== undefined ? (
            <span
              className={SubframeUtils.twClassNames(
                "text-header font-header text-[#171717ff]",
                {
                  "group-active/ec6b5f2a:text-brand-primary group-focus-within/ec6b5f2a:text-brand-primary":
                    variant === "total",
                  "text-[#2563ebff]": variant === "brand",
                  "text-[#ca8a04ff]": variant === "escalated",
                  "text-[#389e0dff]": variant === "resolved",
                  "text-header font-header text-[#cf1322ff]":
                    variant === "at-risk",
                  "text-[#000000ff]": variant === "pending",
                }
              )}
            >
              {value}
            </span>
          ) : null}
          {secondaryValue ? (
            <span
              className={SubframeUtils.twClassNames(
                "text-label-bold font-label-bold text-default-font",
                {
                  "text-brand-700": variant === "brand",
                  "text-warning-600": variant === "escalated" || variant === "pending",
                  "text-success-600": variant === "resolved",
                  "text-error-600": variant === "at-risk",
                }
              )}
            >
              {secondaryValue}
            </span>
          ) : null}
          {secondaryLabel ? (
            <span className="text-label font-label text-default-font">
              {secondaryLabel}
            </span>
          ) : null}
        </div>
        <div
          className={SubframeUtils.twClassNames(
            "hidden h-8 w-8 flex-none items-center justify-center gap-2",
            { flex: loading }
          )}
        >
          <Loader size="large" />
        </div>
      </div>
    </div>
  );
});

export const CustomStatCard = CustomStatCardRoot;
